import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { doc, updateDoc, where } from 'firebase/firestore';
import React, { useMemo, useState } from 'react';
import MicrospecDataTable, { MicrospecDataTableColumn } from '../../../../MicrospecDynamicUI/Components/DataTable/MicrospecFirestoreDataTable';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { BusinessOrderDeliveryMethod, BusinessOrderStatus } from '../../../../MicrospecDynamicUI/Models/Business';
import { ORDERS_COLLECTION } from '../../../constants/collectionPaths';

// ── Config maps ─────────────────────────────────────────────────────────────

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const STATUS_CONFIG: Record<BusinessOrderStatus, { label: string; color: ChipColor }> = {
    pending:               { label: 'ממתין',               color: 'warning'   },
    preparing:             { label: 'בהכנה',               color: 'info'      },
    readyForDelivery:      { label: 'מוכן לאיסוף/משלוח', color: 'primary'   },
    outForDelivery:        { label: 'יצא למשלוח',         color: 'secondary' },
    delivered:             { label: 'נמסר',                color: 'success'   },
    cancelledByUser:       { label: 'בוטל ע"י לקוח',      color: 'error'     },
    cancelledByBusiness:   { label: 'בוטל ע"י העסק',      color: 'error'     },
};

const DELIVERY_CONFIG: Record<BusinessOrderDeliveryMethod, { label: string; color: ChipColor }> = {
    delivery: { label: 'משלוח',      color: 'primary' },
    pickup:   { label: 'איסוף עצמי', color: 'default' },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTimestamp(ts: any): string {
    if (!ts) return '—';
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString('he-IL', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function computeTotal(orderItems: any[]): number {
    if (!Array.isArray(orderItems)) return 0;
    return orderItems.reduce((sum, item) => {
        const addons = (item.specifications ?? []).reduce(
            (s: number, spec: any) => s + (spec.additionalPrice ?? 0), 0
        );
        return sum + (item.basePrice ?? 0) * (item.quantity ?? 1) + addons;
    }, 0);
}

function resolveProductName(name: any): string {
    if (!name) return '—';
    if (typeof name === 'string') return name;
    return (Object.values(name)[0] as string) ?? '—';
}

// ── Order detail dialog ──────────────────────────────────────────────────────

interface OrderDetailDialogProps {
    open: boolean;
    order: any | null;
    onClose: () => void;
    onStatusUpdated: () => void;
    db: any;
}

const OrderDetailDialog: React.FC<OrderDetailDialogProps> = ({ open, order, onClose, onStatusUpdated, db }) => {
    const [status, setStatus] = useState<BusinessOrderStatus>(order?.status ?? 'pending');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    React.useEffect(() => {
        if (order) {
            setStatus(order.status ?? 'pending');
            setSaveError(null);
        }
    }, [order]);

    const handleSave = async () => {
        if (!db || !order?.id) return;
        setSaving(true);
        setSaveError(null);
        try {
            await updateDoc(doc(db, ORDERS_COLLECTION, order.id), { status });
            onStatusUpdated();
            onClose();
        } catch (e) {
            console.error('Order status update error:', e);
            setSaveError('שגיאה בעדכון הסטטוס, אנא נסה שוב');
        } finally {
            setSaving(false);
        }
    };

    if (!order) return null;

    const items: any[] = Array.isArray(order.order) ? order.order : [];
    const total = computeTotal(items);
    const deliveryCfg = DELIVERY_CONFIG[order.delivery_method as BusinessOrderDeliveryMethod]
        ?? { label: order.delivery_method, color: 'default' as ChipColor };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
                <Box>
                    <Typography variant="h6" fontWeight={700}>פרטי הזמנה</Typography>
                    <Typography variant="caption" color="text.secondary">{formatTimestamp(order.created_at)}</Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ mt: 0.5 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* ── Customer details ── */}
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>פרטי לקוח</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5, mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">שם</Typography>
                    <Typography variant="body2">{order.first_name} {order.last_name}</Typography>

                    <Typography variant="body2" color="text.secondary">טלפון</Typography>
                    <Typography variant="body2" dir="ltr">{order.phone_number}</Typography>

                    <Typography variant="body2" color="text.secondary">אספקה</Typography>
                    <Box>
                        <Chip
                            label={deliveryCfg.label}
                            color={deliveryCfg.color}
                            size="small"
                            variant="outlined"
                        />
                    </Box>

                    {order.delivery_method === 'delivery' && order.address && (
                        <>
                            <Typography variant="body2" color="text.secondary">כתובת</Typography>
                            <Typography variant="body2">{order.address}</Typography>
                        </>
                    )}

                    {order.order_note && (
                        <>
                            <Typography variant="body2" color="text.secondary">הערות</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{order.order_note}</Typography>
                        </>
                    )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* ── Order items ── */}
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>פריטים</Typography>
                {items.length === 0 && (
                    <Typography variant="body2" color="text.secondary">אין פריטים</Typography>
                )}
                {items.map((item, idx) => {
                    const name = resolveProductName(item.productName);
                    const itemTotal =
                        (item.basePrice ?? 0) * (item.quantity ?? 1) +
                        (item.specifications ?? []).reduce((s: number, spec: any) => s + (spec.additionalPrice ?? 0), 0);
                    return (
                        <Box key={item.cartItemId ?? idx} sx={{ mb: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" fontWeight={600}>
                                    {name} × {item.quantity}
                                </Typography>
                                <Typography variant="body2" fontWeight={600} color="primary.main">
                                    ₪{itemTotal.toFixed(2)}
                                </Typography>
                            </Box>
                            {(item.specifications ?? [])
                                .filter((s: any) => s.value)
                                .map((spec: any, si: number) => {
                                    const specName = typeof spec.name === 'string'
                                        ? spec.name
                                        : (Object.values(spec.name ?? {})[0] as string);
                                    return (
                                        <Typography
                                            key={si}
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ pl: 1 }}
                                        >
                                            {specName}: {spec.value}
                                        </Typography>
                                    );
                                })}
                        </Box>
                    );
                })}

                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={700}>סה"כ (לפני מע"מ)</Typography>
                    <Typography variant="body2" fontWeight={800} color="primary.main">
                        ₪{total.toFixed(2)}
                    </Typography>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* ── Status update ── */}
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>עדכון סטטוס</Typography>
                <FormControl fullWidth size="small">
                    <InputLabel>סטטוס</InputLabel>
                    <Select
                        value={status}
                        label="סטטוס"
                        onChange={e => setStatus(e.target.value as BusinessOrderStatus)}
                    >
                        {(Object.entries(STATUS_CONFIG) as [BusinessOrderStatus, { label: string; color: ChipColor }][]).map(
                            ([value, cfg]) => (
                                <MenuItem key={value} value={value}>
                                    <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" sx={{ cursor: 'pointer' }} />
                                </MenuItem>
                            )
                        )}
                    </Select>
                </FormControl>

                {saveError && (
                    <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                        {saveError}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={saving}
                        sx={{ textTransform: 'none', minWidth: 80 }}
                    >
                        סגור
                    </Button>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSave}
                        disabled={saving || status === order.status}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        {saving ? 'שומר...' : 'שמור סטטוס'}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

// ── Main page ────────────────────────────────────────────────────────────────

interface OrdersManagementPageProps {
    statuses: BusinessOrderStatus[];
    title?: string;
    subtitle?: string;
}

const OrdersManagementPage: React.FC<OrdersManagementPageProps> = ({
    statuses,
    title = 'הזמנות',
    subtitle = 'ניהול וצפייה בהזמנות שהתקבלו',
}) => {
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const db = cloudProvider.firestore?.instance;
    const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // Stable where clause — memoised so the DataTable ref comparison stays stable
    const statusWheres = useMemo(() => [where('status', 'in', statuses)], [statuses]);

    const columns: MicrospecDataTableColumn[] = [
        {
            field: 'created_at',
            headerName: 'תאריך',
            sortable: true,
            minWidth: 150,
            align: 'right',
            render: (value: any) => formatTimestamp(value),
        },
        {
            field: 'first_name',
            headerName: 'לקוח',
            sortable: true,
            minWidth: 160,
            align: 'right',
            render: (_: any, row: any) => `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim(),
        },
        {
            field: 'phone_number',
            headerName: 'טלפון',
            sortable: false,
            minWidth: 130,
            align: 'center',
            render: (value: string) => (
                <Typography variant="body2" dir="ltr">{value ?? '—'}</Typography>
            ),
        },
        {
            field: 'delivery_method',
            headerName: 'אספקה',
            sortable: true,
            minWidth: 120,
            align: 'center',
            render: (value: BusinessOrderDeliveryMethod) => {
                const cfg = DELIVERY_CONFIG[value] ?? { label: value, color: 'default' as ChipColor };
                return <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
            },
        },
        {
            field: 'status',
            headerName: 'סטטוס',
            sortable: true,
            minWidth: 170,
            align: 'center',
            render: (value: BusinessOrderStatus) => {
                const cfg = STATUS_CONFIG[value] ?? { label: value, color: 'default' as ChipColor };
                return <Chip label={cfg.label} color={cfg.color} size="small" variant="filled" />;
            },
        },
        {
            field: 'order',
            headerName: 'סכום / פריטים',
            sortable: false,
            minWidth: 160,
            align: 'center',
            render: (value: any[]) => {
                const count = Array.isArray(value) ? value.length : 0;
                const total = computeTotal(value);
                return `₪${total.toFixed(2)} · ${count} פריטים`;
            },
        },
    ];

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <MicrospecDataTable
                key={`${statuses.join(',')}-${refreshKey}`}
                collection={ORDERS_COLLECTION}
                columns={columns}
                wheres={statusWheres}
                title={title}
                subtitle={subtitle}
                searchable={true}
                exportable={true}
                refreshable={true}
                pageSize={25}
                maxHeight={700}
                onRowClick={(row: any) => setSelectedOrder(row)}
                actions={[
                    {
                        icon: <AssignmentIcon />,
                        label: 'פרטים ועדכון סטטוס',
                        onClick: (row: any) => setSelectedOrder(row),
                    },
                ]}
            />
            <OrderDetailDialog
                open={!!selectedOrder}
                order={selectedOrder}
                onClose={() => setSelectedOrder(null)}
                onStatusUpdated={() => setRefreshKey(prev => prev + 1)}
                db={db}
            />
        </Box>
    );
};

export default OrdersManagementPage;
